import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type OnboardingChoice } from '@apm/contracts';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ErrorState, Skeleton } from '../components/ui/states';
import { authApi } from '../features/auth/api';
import { useAuth } from '../features/auth/auth-context';
import { guestApi } from '../features/guests/api';
import { issueApi } from '../features/issues/api';
import { orgInviteApi, orgInvitePreviewKey } from '../features/organizations/api';
import { OrgInviteCard } from '../features/organizations/components/org-invite-card';
import { cn } from '../lib/utils';
import { displayDate } from '../lib/date';
import { errorMessage } from '../lib/error-message';
import { ApiRequestError } from '../lib/http';

/**
 * Hoàn tất tài khoản sau khi Google xác minh một email chưa có tài khoản —
 * APM-GAUTH-a/b (`docs/plan-google-signup.md`), ba lựa chọn từ APM-PSPACE-m.
 *
 * ## Vì sao là một route công khai, không nằm dưới `_app`
 *
 * Người mở trang này **chưa có tài khoản**. Mọi thứ dưới `_app` đều giả định có
 * phiên: thanh điều hướng, bộ chọn tổ chức, truy vấn `/me/abilities`. Đặt trang
 * vào đó là dựng cả một cái vỏ ứng dụng quanh một người chưa vào được.
 *
 * ## Không có ô dán link mời
 *
 * Từng có, và nó thừa: người ta **bấm** link mời chứ không đi chép mã trong đó.
 * Cú bấm ấy đưa họ qua `/login?next=/join/<token>` → Google → và callback mang
 * token sang đây trong `?invite=`. Ai cũng đến bằng đường ấy, nên một ô nhập
 * thủ công chỉ là một câu hỏi không ai có sẵn câu trả lời.
 */
export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
  validateSearch: z.object({
    invite: z.string().optional().catch(undefined),
    /** Link mời cộng tác viên vào một dự án — APM-GAUTH-e. */
    guestInvite: z.string().optional().catch(undefined),
    /** Link mời khách hàng vào một dự án — APM-GAUTH-e. */
    clientInvite: z.string().optional().catch(undefined),
  }),
});

/** Thẻ đã chọn ở màn ba lựa chọn: thẻ 3 là `guest_only` của hợp đồng. */
type Picked = OnboardingChoice | null;

/**
 * Người đăng nhập Google lần đầu, chưa có tài khoản — APM-GAUTH-a…e, APM-PSPACE-m
 * (docs/plan-personal-space.md §0.3).
 *
 * - **Link khách hàng** (`clientInvite`) hoặc **link tổ chức không cần duyệt**: xác nhận tên
 *   rồi vào, không ba thẻ (không đổi).
 * - **Không link**, **link tổ chức cần duyệt**, **link cộng tác viên** ⇒ màn "Bạn cần thuộc một
 *   tổ chức" với ba thẻ: (1) tổ chức chỉ có mình tôi, (2) tổ chức đầy đủ — "sau này sẽ tính
 *   phí", (3) chờ được mời. Với link cộng tác viên, thẻ 3 đổi thành "Chỉ tham gia dự án được
 *   mời" (nhà là tổ chức của dự án), vì đơn cộng tác viên cần một tài khoản.
 * - Thẻ 3 không link / link tổ chức cần duyệt ⇒ **phòng chờ** (APM-GAUTH-e): chưa có tài khoản,
 *   email để gửi quản trị, đơn đang chờ, "Kiểm tra lại".
 */
function OnboardingPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invite, guestInvite, clientInvite } = Route.useSearch();
  const { status, user, completeOnboarding, enterFromOnboarding } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [picked, setPicked] = useState<Picked>(null);
  const [waiting, setWaiting] = useState(false);

  const projectLink = guestInvite
    ? ({ kind: 'guest', token: guestInvite } as const)
    : clientInvite
      ? ({ kind: 'client', token: clientInvite } as const)
      : null;

  const info = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => authApi.onboardingTicket(),
    retry: false,
  });

  const invited = useQuery({
    queryKey: orgInvitePreviewKey(invite ?? ''),
    queryFn: () => orgInviteApi.preview(invite!),
    enabled: invite !== undefined,
    retry: false,
  });

  const linked = useQuery({
    queryKey: ['onboarding', 'project-link', projectLink?.kind, projectLink?.token],
    queryFn: () =>
      projectLink!.kind === 'guest'
        ? guestApi.preview(projectLink!.token)
        : issueApi.previewClientInvite(projectLink!.token),
    enabled: projectLink !== null,
    retry: false,
  });

  // Tên Google chỉ **điền sẵn**, và chỉ khi người dùng chưa gõ gì: ghi đè sau
  // đó sẽ xoá mất thứ họ đang gõ dở đúng lúc truy vấn trả về.
  useEffect(() => {
    if (info.data?.suggestedName) setDisplayName((current) => current || info.data.suggestedName!);
  }, [info.data]);

  /*
    Quay lại trang với phiếu đã ở phòng chờ (đơn đang chờ, hoặc đã được mời) ⇒ mở thẳng phòng
    chờ thay vì hỏi lại ba thẻ. State chứ không phải search param: phiếu (cookie 24 giờ) đã
    nhớ đủ, và "đã chọn thẻ 3" chỉ có nghĩa khi server có gì để báo.
  */
  const resumable =
    info.data !== undefined &&
    (info.data.accountReady || info.data.joinRequests.some((r) => r.status === 'pending'));
  useEffect(() => {
    if (resumable && projectLink === null) setWaiting(true);
  }, [resumable, projectLink]);

  /*
    Có phiên rồi (vừa xong, hoặc bấm Back quay lại đây) ⇒ đi tiếp. Đích theo **phiên vừa
    mở**, không theo link: thẻ 1 mở phiên ở không gian cá nhân, và chỉ phiên mới biết điều
    đó. Khách hàng / cộng tác viên về `/` để `_app` đưa đúng chỗ.
  */
  useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    void navigate({
      to: user.isPersonal && user.orgRole !== 'CLIENT' ? '/projects' : '/',
      replace: true,
    });
  }, [status, user, navigate]);

  const joining = invite !== undefined && invited.isSuccess;
  const joiningProject = projectLink !== null && linked.isSuccess;
  const guestLink = joiningProject && projectLink.kind === 'guest' ? linked.data : null;
  const approvalLink = joining && invited.data.requiresApproval ? invited.data : null;

  const submit = useMutation({
    mutationFn: (choice: OnboardingChoice | undefined) =>
      completeOnboarding({
        displayName: displayName.trim(),
        ...(choice !== undefined ? { choice } : {}),
        ...(choice === 'full' ? { orgName: orgName.trim() } : {}),
        // Nhiều nhất MỘT link — hợp đồng từ chối nhiều hơn một. Link chết thì không gửi:
        // người dùng đã thấy dải "link không còn hiệu lực" và chọn một thẻ thay vào.
        ...(joiningProject
          ? projectLink.kind === 'guest'
            ? { guestInviteToken: projectLink.token }
            : { clientInviteToken: projectLink.token }
          : joining
            ? { inviteToken: invite }
            : {}),
      }),
    // Thẻ 3 với link tổ chức cần duyệt: chưa có tài khoản ⇒ ở lại đây, sang phòng chờ.
    onSuccess: (pending) => {
      if (pending === null) return;
      setWaiting(true);
      void info.refetch();
    },
  });

  // Phòng chờ — APM-GAUTH-e. 404 `ONBOARDING_NOT_INVITED` là kết cục thường gặp nhất
  // ("chưa ai mời"), nói bằng một dòng chứ không phải lỗi đỏ.
  const enter = useMutation({
    mutationFn: () => enterFromOnboarding(),
    onSettled: () => void info.refetch(),
  });
  const notInvitedYet =
    enter.error instanceof ApiRequestError &&
    enter.error.details?.['reason'] === 'ONBOARDING_NOT_INVITED';

  if (info.isError) {
    return (
      <Shell title={t('onboarding.expiredTitle')} description={t('onboarding.expiredHelp')}>
        <Button onClick={() => void navigate({ to: '/login' })}>
          {t('onboarding.backToLogin')}
        </Button>
      </Shell>
    );
  }

  // Chờ đủ: hiện một màn rồi mới đổi sang màn khác là một cú nháy làm người dùng tưởng mình
  // bấm nhầm chỗ.
  if (
    info.isPending ||
    (invite !== undefined && invited.isPending) ||
    (projectLink !== null && linked.isPending)
  ) {
    return (
      <Shell title={t('onboarding.title')} description={t('onboarding.subtitle')}>
        <Skeleton className="h-32 w-full" />
      </Shell>
    );
  }

  const signedInAs = (
    <p className="rounded-DEFAULT bg-muted p-2 text-sm">
      {t('onboarding.signedInAs')} <strong>{info.data.email}</strong>
    </p>
  );

  // ── Phòng chờ (thẻ 3) ─────────────────────────────────────────────────────
  if (waiting) {
    const pending = submit.data ?? null;
    return (
      <Shell
        title={pending ? t('onboarding.pendingTitle') : t('onboarding.waitingTitle')}
        description={
          pending
            ? t('onboarding.pendingHelpWait', { org: pending.orgName })
            : t('onboarding.waitingSubtitle')
        }
      >
        {signedInAs}

        {info.data.accountReady ? (
          <div className="flex flex-col gap-2 rounded-DEFAULT border border-rag-green/50 bg-rag-green/10 p-3">
            <p className="text-sm font-medium">{t('onboarding.readyTitle')}</p>
            <Button disabled={enter.isPending} onClick={() => enter.mutate()}>
              {t('onboarding.enterNow')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 rounded-DEFAULT border border-border p-3 text-sm">
            <p>{t('onboarding.waitSendEmail')}</p>
            <code className="rounded-DEFAULT bg-muted px-2 py-1 font-mono text-xs">
              {info.data.email}
            </code>
            <p className="text-xs text-muted-foreground">{t('onboarding.waitThenCheck')}</p>
            <Button disabled={enter.isPending} onClick={() => enter.mutate()}>
              {enter.isPending ? t('onboarding.checking') : t('onboarding.checkAgain')}
            </Button>
            {notInvitedYet && (
              <p role="status" className="text-xs text-muted-foreground">
                {t('onboarding.notInvitedYet')}
              </p>
            )}
            {enter.isError && !notInvitedYet && (
              <ErrorState message={errorMessage(enter.error, t)} />
            )}
          </div>
        )}

        {info.data.joinRequests.length > 0 && (
          <ul className="flex flex-col gap-1" aria-label={t('onboarding.requestsLabel')}>
            {info.data.joinRequests.map((request) => (
              <li
                key={`${request.orgName}-${request.createdAt}`}
                className={
                  request.status === 'pending'
                    ? 'rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 p-2 text-xs'
                    : 'rounded-DEFAULT border border-border bg-muted p-2 text-xs'
                }
              >
                {t(`onboarding.request.${request.status}`, {
                  org: request.orgName,
                  at: displayDate(request.createdAt),
                })}
              </li>
            ))}
          </ul>
        )}

        {/*
          Họ được phép đóng trang — phiếu nằm trong cookie và sống 24 giờ. Không nói ra
          thì người ta ngồi giữ tab mở vì sợ mất.
        */}
        <p className="text-xs text-muted-foreground">{t('onboarding.waitClosable')}</p>

        <button
          type="button"
          className="self-start text-xs text-muted-foreground underline"
          onClick={() => {
            submit.reset();
            setPicked(null);
            setWaiting(false);
          }}
        >
          {t('onboarding.backToChoices')}
        </button>
      </Shell>
    );
  }

  const nameField = (
    <label className="flex flex-col gap-1 text-sm">
      {t('onboarding.displayName')}
      <input
        className="h-9 rounded-control border border-border-strong bg-background px-2"
        autoFocus
        value={displayName}
        maxLength={200}
        onChange={(event) => setDisplayName(event.target.value)}
        placeholder={t('onboarding.displayNamePlaceholder')}
      />
    </label>
  );
  const submitError = submit.isError && <ErrorState message={errorMessage(submit.error, t)} />;

  // ── Link khách hàng: không ba thẻ (không đổi) ─────────────────────────────
  if (joiningProject && projectLink.kind === 'client') {
    return (
      <Shell
        title={t('clientInvite.heading', { project: linked.data.projectName })}
        description={t('clientInvite.subheading', {
          org: linked.data.orgName,
          key: linked.data.projectKey,
        })}
      >
        {signedInAs}
        <form
          className="flex flex-col gap-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!submit.isPending) submit.mutate(undefined);
          }}
        >
          {nameField}
          <p className="text-xs text-muted-foreground">{t('clientInvite.registerHelp')}</p>
          {submitError}
          <Button type="submit" disabled={!displayName.trim() || submit.isPending}>
            {submit.isPending ? t('onboarding.creating') : t('clientInvite.register')}
          </Button>
        </form>
      </Shell>
    );
  }

  // ── Link tổ chức không cần duyệt: vào thẳng (không đổi) ───────────────────
  if (joining && !approvalLink) {
    return (
      <Shell title={t('onboarding.invitedTitle')} description={t('onboarding.invitedSubtitle')}>
        {signedInAs}
        {/* Cùng thẻ với `/join` và trang đăng nhập — APM-ORGP-b. */}
        <OrgInviteCard preview={invited.data} />
        <form
          className="flex flex-col gap-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!submit.isPending) submit.mutate(undefined);
          }}
        >
          {nameField}
          {submitError}
          <Button type="submit" disabled={!displayName.trim() || submit.isPending}>
            {submit.isPending ? t('onboarding.creating') : t('onboarding.join')}
          </Button>
        </form>
      </Shell>
    );
  }

  /*
    ── Link tổ chức cần duyệt: một nút gửi yêu cầu (APM-ORGJR-c) ───────────────

    Trước đây link này rơi vào ba thẻ "tổ chức của riêng tôi / tổ chức đầy đủ / chờ được
    mời", và đơn chỉ ra đời sau khi chọn một thẻ rồi bấm. Prod 22/10/2026: người được mời
    dừng ở đó, đơn không bao giờ được gửi, quản trị không thấy gì để duyệt và phải mời lại
    bằng email. Người cầm link cần duyệt đã biết mình muốn vào đâu — hỏi thêm "bắt đầu
    thế nào" chỉ làm họ lạc. Gửi đơn theo đường phòng chờ (`guest_only`: chưa tạo tài khoản,
    duyệt xong mới có), đúng như thẻ 3 vẫn làm.
  */
  if (approvalLink) {
    return (
      <Shell
        title={t('onboarding.requestTitle', { org: approvalLink.orgName })}
        description={t('onboarding.requestSubtitle')}
      >
        {signedInAs}
        <OrgInviteCard preview={approvalLink} />
        <form
          className="flex flex-col gap-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!submit.isPending) submit.mutate('guest_only');
          }}
        >
          {nameField}
          {submitError}
          <Button type="submit" disabled={!displayName.trim() || submit.isPending}>
            {submit.isPending ? t('onboarding.creating') : t('onboarding.requestSubmit')}
          </Button>
        </form>
      </Shell>
    );
  }

  // ── Ba lựa chọn: không link, link cộng tác viên ─────────────────────────
  const deadLink =
    (invite !== undefined && invited.isError) || (projectLink !== null && linked.isError);
  /*
    Thẻ 3 không gọi lệnh khi không có link nào để gửi đơn: phòng chờ chỉ là phiếu (không
    tài khoản). Có link cộng tác viên thì gọi để dựng tài khoản `GUEST` ở tổ chức của dự án.
    Link tổ chức cần duyệt không tới đây nữa — màn riêng ở trên (APM-ORGJR-c).
  */
  const thirdIsWaitOnly = guestLink === null;
  const cards: { choice: OnboardingChoice; title: string; hint: string; badge?: string }[] = [
    {
      choice: 'personal',
      title: t('onboarding.choice.personal.title'),
      hint: t('onboarding.choice.personal.hint'),
    },
    {
      choice: 'full',
      title: t('onboarding.choice.full.title'),
      hint: t('onboarding.choice.full.hint'),
      badge: t('onboarding.choice.full.badge'),
    },
    guestLink
      ? {
          choice: 'guest_only',
          title: t('onboarding.choice.guestOnly.title'),
          hint: t('onboarding.choice.guestOnly.hint', { project: guestLink.projectName }),
        }
      : {
          choice: 'guest_only',
          title: t('onboarding.choice.wait.title'),
          hint: t('onboarding.choice.wait.hint'),
        },
  ];

  const needsName = !(picked === 'guest_only' && thirdIsWaitOnly);
  const ready =
    picked !== null &&
    (!needsName || displayName.trim() !== '') &&
    (picked !== 'full' || orgName.trim() !== '');
  const submitLabel =
    picked === 'personal'
      ? t('onboarding.choice.personal.submit')
      : picked === 'full'
        ? t('onboarding.choice.full.submit')
        : guestLink
          ? t('onboarding.choice.guestOnly.submit')
          : t('onboarding.choice.wait.submit');

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || submit.isPending) return;
    if (picked === 'guest_only' && thirdIsWaitOnly) {
      setWaiting(true);
      return;
    }
    submit.mutate(picked!);
  };

  return (
    <Shell title={t('onboarding.needOrgTitle')} description={t('onboarding.needOrg')}>
      {signedInAs}

      {/* Link mời có mà chết: nói ra, rồi để họ chọn thay vì mắc kẹt. */}
      {deadLink && (
        <div className="rounded-DEFAULT border border-rag-amber/50 bg-rag-amber/10 p-3">
          <p className="text-sm font-medium text-rag-amber">{t('onboarding.inviteDead')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('onboarding.inviteDeadHelp')}</p>
        </div>
      )}
      {guestLink && (
        <p
          role="status"
          className="rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 p-2 text-xs"
        >
          {t('onboarding.guestLinkPending', {
            project: guestLink.projectName,
            org: guestLink.orgName,
          })}
        </p>
      )}

      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <div
          role="radiogroup"
          aria-label={t('onboarding.choicesLabel')}
          className="flex flex-col gap-2"
        >
          {cards.map((card) => (
            <button
              key={card.choice}
              type="button"
              role="radio"
              aria-checked={picked === card.choice}
              data-testid={`onboarding-choice-${card.choice}`}
              onClick={() => setPicked(card.choice)}
              className={cn(
                'flex flex-col gap-1 rounded-DEFAULT border p-3 text-left transition-colors',
                picked === card.choice
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                {card.title}
                {card.badge && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-normal text-muted-foreground">
                    {card.badge}
                  </span>
                )}
              </span>
              <span className="text-xs text-muted-foreground">{card.hint}</span>
            </button>
          ))}
        </div>

        {picked !== null && needsName && nameField}
        {picked === 'full' && (
          <label className="flex flex-col gap-1 text-sm">
            {t('onboarding.orgName')}
            <input
              className="h-9 rounded-control border border-border-strong bg-background px-2"
              value={orgName}
              maxLength={200}
              onChange={(event) => setOrgName(event.target.value)}
              placeholder={t('onboarding.orgNamePlaceholder')}
            />
            <span className="text-xs text-muted-foreground">{t('onboarding.orgNameHelp')}</span>
          </label>
        )}

        {submitError}
        {picked !== null && (
          <Button type="submit" disabled={!ready || submit.isPending}>
            {submit.isPending ? t('onboarding.creating') : submitLabel}
          </Button>
        )}
      </form>
    </Shell>
  );
}

function Shell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}): ReactNode {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-muted p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">{children}</CardContent>
      </Card>
    </main>
  );
}
